import * as Yup from 'yup'
import { PAYMENT_METHOD, SALES_METHOD } from '../../constants'

const requiredNotDash = (label) =>
  Yup.string()
    .transform((v) => (v ?? '').toString().trim())
    .test('not-empty', `${label} wajib diisi`, (v) => !!v && v !== '-')
    .required(`${label} wajib diisi`)

const getPaymentMethodValue = (paymentMethod) => {
  if (!paymentMethod) return null
  if (typeof paymentMethod === 'string') return paymentMethod.trim() || null
  return paymentMethod?.value || null
}

const isMidtransCreditCardPaymentMethod = (paymentMethod) => {
  return getPaymentMethodValue(paymentMethod) === PAYMENT_METHOD.CREDIT_CARD
}

const SalesDealsSchema = Yup.object().shape({
  use_existing_user: Yup.boolean().default(true).required('Required'),
  edit_existing_user: Yup.boolean().default(false).required('Required'),

  user_id: Yup.number().integer().nullable(),
  email: Yup.string().trim().nullable(),
  password: Yup.string().trim().nullable(),
  name: Yup.string().trim().nullable(),
  phone: Yup.string().trim().nullable(),
  phone_code: Yup.string().trim().nullable(),

  complete_address: Yup.string().trim().nullable(),
  recipient_name: Yup.string().trim().nullable(),
  recipient_number: Yup.string().trim().nullable(),
  instruction: Yup.string().trim().nullable(),

  latitude: Yup.number()
    .min(-90, 'Latitude harus lebih besar atau sama dengan -90')
    .max(90, 'Latitude harus lebih kecil atau sama dengan 90')
    .nullable(),

  longitude: Yup.number()
    .min(-180, 'Longitude harus lebih besar atau sama dengan -180')
    .max(180, 'Longitude harus lebih kecil atau sama dengan 180')
    .nullable(),

  items: Yup.array()
    .of(
      Yup.object().shape({
        product_id: Yup.number().integer().required('Required'),
        selling_price: Yup.number().positive().required('Required'),
        authenticator: Yup.string().trim().nullable(true),
        authenticity_check_note: Yup.string().trim().nullable(true),
        include_authenticity_check: Yup.boolean().default(false),
        note: Yup.string().trim().nullable(true),
      })
    )
    .min(1, 'At least one product must be selected')
    .required('Items are required'),

  courier: Yup.object()
    .shape({
      rate: Yup.object().shape({
        id: Yup.number().integer().required('Required'),
      }),
    })
    .nullable(),

  payment_method: Yup.object()
    .shape({
      value: Yup.string().trim().required('Required'),
      installment: Yup.number().integer().nullable(),
    })
    .nullable(),

  voucher_code: Yup.string().trim().nullable(true),
  sales_method_note: Yup.string()
    .trim()
    .nullable(true)
    .when('sales_method', {
      is: SALES_METHOD.CONTENT_INFLUENCER,
      then: (schema) => schema.required('Influencer Name wajib diisi untuk sales method (M) Content Influencer'),
      otherwise: (schema) => schema.nullable(true),
    }),

  // ✅ Wajib & tidak boleh "-"
  sales_name_in_invoice: requiredNotDash('Nama Sales di Invoice'),
  deal_by: requiredNotDash('Deal By'),

  // ✅ Assist By berasal dari list, jadi validate list-nya
  assist_by_list: Yup.array()
    .of(requiredNotDash('Assist By'))
    .min(1, 'Assist By minimal 1')
    .test('at-least-one-filled', 'Assist By minimal 1', (arr) => {
      const list = Array.isArray(arr) ? arr : []
      return list.some((x) => {
        const v = (x ?? '').toString().trim()
        return v && v !== '-'
      })
    }),

  // ✅ Optional tapi bagus: pastikan hasil join juga valid
  assist_by: Yup.string()
    .transform((v) => (v ?? '').toString().trim())
    .test('assist-not-empty', 'Assist By minimal 1', (v) => !!v && v !== '-')
    .nullable(true),

  note: Yup.string().trim().nullable(true),
  pickup_at: Yup.date().nullable(true),

  include_invoice: Yup.boolean().default(true),
  include_polaroid: Yup.boolean().default(false),
  include_love_letter: Yup.boolean().default(false),

  down_payment: Yup.mixed().test(
    'down-payment-conditional',
    'Mohon Isi Down Payment (DP).',
    function (value) {
      const isMidtransCC = isMidtransCreditCardPaymentMethod(this.parent.payment_method)

      if (isMidtransCC) {
        if (value === null || value === undefined || value === '') return true
        const n = Number(value)
        return Number.isFinite(n) && n > 0
      }

      const n = Number(value)
      return Number.isFinite(n) && n > 0
    }
  ),

  dp_transfer_proofs: Yup.mixed().test(
    'dp-transfer-proofs-conditional',
    'Upload bukti transfer DP.',
    function (value) {
      const isMidtransCC = isMidtransCreditCardPaymentMethod(this.parent.payment_method)

      if (isMidtransCC) return true

      return Array.isArray(value) && value.length > 0
    }
  ),
})

export default SalesDealsSchema
